import moment from 'moment';

export const IN_PERSON_LABEL = 'Consider in-person assessment';

export const getMocaPromptSource = (latestMoca) => {
  if (!latestMoca) return '';
  const date = latestMoca.takenOn ? ` on ${moment(latestMoca.takenOn).format('MMM D')}` : '';
  return `Remote MoCA ${Math.round(latestMoca.score)}/${latestMoca.maxScore}${date}, recorded in Mindy Review. First visit since.`;
};

export const formatStamp = (stamp) =>
  `${stamp.place} · ${stamp.by} · ${moment(stamp.at).format('h:mm A')}`;

const TEAM_LABELS = {
  plan: 'In-person MoCA planned',
  skip: 'Not needed this visit',
};

export const getTeamLabel = (team) =>
  team.choice === 'edit' ? `Edited (“${team.note}”)` : TEAM_LABELS[team.choice];

export const TEAM_BADGE_LABELS = {
  plan: 'In-person MoCA planned',
  skip: 'Not needed this visit',
  edit: 'Edited',
};

const PROVIDER_LABELS = {
  agree: 'Agreed',
  plan: 'In-person MoCA planned',
  skip: 'Not needed this visit',
};

export const getProviderLabel = (provider, outcome) => {
  if (provider.choice === 'edit') return `Edited: \u201c${provider.note}\u201d`;
  if (provider.choice === 'disagree') {
    return outcome === 'in_person'
      ? 'Disagreed: give an in-person MoCA today'
      : 'Disagreed: in-person MoCA not needed today';
  }
  return PROVIDER_LABELS[provider.choice];
};

export const formatPersonStamp = (stamp) =>
  `${stamp.by} \u00b7 ${moment(stamp.at).format('h:mm A')}`;

export const formatFinalCall = (provider) => `Final call \u00b7 ${formatPersonStamp(provider)}`;

export const OUTCOME_LABELS = {
  in_person: 'In-person MoCA planned',
  not_needed: 'Not needed this visit',
  custom: 'Edited',
};

const STATE_KIND_LABELS = {
  in_person: 'In-person MoCA planned',
  not_needed: 'MoCA: not needed',
  custom: 'MoCA plan edited',
};

const KIND_TONES = { in_person: 'success', not_needed: 'outline', custom: 'neutral' };

export const getOutcomeState = (state) => {
  if (state.outcome === 'undecided') return { tone: 'warning', label: 'In-person MoCA?' };
  const label = STATE_KIND_LABELS[state.outcome];
  if (state.changedByProvider) {
    return { tone: 'warning', label: `Changed by provider \u00b7 ${label}` };
  }
  return { tone: KIND_TONES[state.outcome], label };
};

export const getTeamStamp = (team) =>
  `${TEAM_BADGE_LABELS[team.choice]}${team.note ? ` (\u201c${team.note}\u201d)` : ''} \u00b7 ${formatStamp(team)}`;

export const getProviderLineForTeam = (state) => {
  const { provider, outcome } = state;
  if (provider.choice === 'agree') return `\u2713 ${provider.by} agreed`;
  if (provider.choice === 'plan' || provider.choice === 'skip') {
    return `Decided by ${provider.by} in the Provider HUD: ${TEAM_BADGE_LABELS[provider.choice]}`;
  }
  if (provider.choice === 'edit')
    return `${provider.by} changed the plan: \u201c${provider.note}\u201d`;
  const label = getProviderLabel(provider, outcome).replace(
    /^Disagreed/,
    `${provider.by} disagreed`,
  );
  return provider.reason ? `${label}. Reason: \u201c${provider.reason}\u201d` : label;
};

export const getDecisionRecord = (state) => {
  const { team, provider, outcome, decidedByProvider } = state;
  if (decidedByProvider) {
    return `${OUTCOME_LABELS[outcome]} \u00b7 Decided by ${provider.by} in the Provider HUD`;
  }
  if (!team) return '';
  const teamPart = getTeamStamp(team);
  if (!provider) return teamPart;
  if (provider.choice === 'agree') return `${teamPart} \u00b7 \u2713 ${provider.by} agreed`;
  return `${teamPart} \u00b7 ${provider.by}: ${getProviderLabel(provider, outcome)}`;
};

const AFTER_VISIT_TONES = { success: 'planned', warning: 'warning', neutral: 'neutral' };

export const getOutcomeTone = (afterVisit) => AFTER_VISIT_TONES[afterVisit.tone];

export const getNoticeLabel = (state) =>
  state.outcome === 'undecided' ? IN_PERSON_LABEL : getOutcomeState(state).label;

export const getNoticeDetail = (latestMoca, state) => {
  const stamp = state.provider ?? state.team;
  const note =
    state.outcome === 'custom' && state.outcomeNote ? ` \u201c${state.outcomeNote}\u201d` : '';
  return stamp
    ? `${getMocaPromptSource(latestMoca)}${note} ${formatStamp(stamp)}`
    : getMocaPromptSource(latestMoca);
};

export const getNoticeTone = (state) => {
  if (state.outcome === 'undecided' || state.changedByProvider) return 'warning';
  return state.outcome === 'in_person' ? 'planned' : 'neutral';
};
